/*
 * Copyright (c) 2024 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Tuesday, August 22nd 2023, 1:31:46 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import { useTranslation } from "react-i18next";
import Stack from "@mui/material/Stack";
import { useGlobalScope } from "core/providers";
import { IntegrationCredentialStore } from "lib/schemas";
import { getDefaultCredentialStore } from "lib/factories";
import { useIntegrationsAPI } from "integrations/services/IntegrationsAPI";
import { SecurityIcon } from "theme/icons";
import { COLORSCHEME } from "theme/styles";
import { OSActionButton, OSDialog, OSLoadingButton } from "ui-widgets";
import { TextFormInput, FormInputCustomErrorWrapper, JSONFormInput, OSAPIForm } from "ui-widgets/forms";

export interface CredentialStoreFormProps extends Pick<React.ComponentProps<typeof OSAPIForm>, "onFormCancel"> {
  newCredentialStoreName?: string;
  credentialStoreId?: IntegrationCredentialStore["id"];
  onSaveCredentialStore?: (credentialStore: IntegrationCredentialStore) => void;
}

export const CredentialStoreForm = ({
  newCredentialStoreName,
  credentialStoreId,
  onSaveCredentialStore,
  ...props
}: CredentialStoreFormProps) => {
  const {
    globalState: { selectedOrganization },
  } = useGlobalScope();

  const defaultNewCredentialStore = getDefaultCredentialStore({
    defaultName: newCredentialStoreName ?? `${selectedOrganization.name} credentials`,
    organization: selectedOrganization,
  });

  const { useCredentialStoreAPIForm } = useIntegrationsAPI();
  const useAPIFormReturn = useCredentialStoreAPIForm({
    credentialStoreDefaultValues: defaultNewCredentialStore,
    credentialStoreFetchId: credentialStoreId,
    updateCredentialStoreFromAPICallback: (credentialStore) => onSaveCredentialStore?.(credentialStore),
  });

  return (
    <OSAPIForm useAPIFormReturn={useAPIFormReturn} {...props}>
      <TextFormInput<IntegrationCredentialStore> schema_field_key="name" label="Name" />
      <Stack direction="row" spacing={2}>
        <JSONFormInput<IntegrationCredentialStore> schema_field_key="credentials" label="Credentials" />
        {credentialStoreId ? (
          <div>
            <DecryptedCredentials credentialStoreId={credentialStoreId} />
          </div>
        ) : null}
      </Stack>
    </OSAPIForm>
  );
};

const DecryptedCredentials = ({ credentialStoreId }) => {
  const { t } = useTranslation();
  const [decryptedCredentials, setDecryptedCredentials] = React.useState<any>();
  const [password, setPassword] = React.useState<string>();
  const { useDecryptCredentials } = useIntegrationsAPI();

  const { decryptCredentials, isMakingAPIRequest, isAPIError, apiErrors } = useDecryptCredentials();

  const onSetPassword = () => {
    decryptCredentials(credentialStoreId, password, setDecryptedCredentials);
  };

  return (
    <OSDialog
      maxWidth="sm"
      renderOpenButton={({ handleOpen }) => (
        <OSActionButton
          actionStyle="custom"
          customIcon={<SecurityIcon width={20} height={20} fill={COLORSCHEME.primary} />}
          size="large"
          onClick={() => {
            handleOpen();
          }}
        >
          {t("Decrypt")}
        </OSActionButton>
      )}
    >
      <Stack sx={{ padding: 8 }}>
        <Stack direction="row" spacing={2} sx={{ alignItems: "center" }}>
          {(() => {
            if (decryptedCredentials)
              return (
                <pre style={{ backgroundColor: "var(--grey-01", padding: 8, borderRadius: 6 }}>
                  {JSON.stringify(decryptedCredentials, null, 2)}
                </pre>
              );
            return (
              <>
                <TextFormInput
                  type="password"
                  onChange={(e) => setPassword(e.target.value)}
                  label={t("Enter Your Password")}
                  schema_field_key="password"
                  sx={{ width: 300, flex: 4 }}
                />
                <OSLoadingButton
                  isLoading={isMakingAPIRequest}
                  variant="contained"
                  sx={{ height: 40, flex: 1 }}
                  onClick={onSetPassword}
                >
                  {t("Decrypt")}
                </OSLoadingButton>
              </>
            );
          })()}
        </Stack>
        {isAPIError && <FormInputCustomErrorWrapper>{apiErrors.detail}</FormInputCustomErrorWrapper>}
      </Stack>
    </OSDialog>
  );
};
